import React, { useCallback } from "react";
import ReactDOM from "react-dom";
import clsx from "clsx";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DraggableProvided,
  DraggableStateSnapshot,
  DropResult,
  DragStart,
  ResponderProvided,
} from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import { HvBulkActions, ActionGeneric } from "@hitachivantara/uikit-react-core";
import {
  HvTable,
  HvTableCell,
  HvTableBody,
  HvTableContainer,
  HvTableHead,
  HvTableHeader,
  HvTableRow,
  HvTableRowProps,
  useHvTable,
  HvTableColumnConfig,
  useHvBulkActions,
  useHvRowSelection,
} from "@hitachivantara/uikit-react-lab";
import {
  HvRowInstance,
  HvTableCellProps,
  HvTableOptions,
} from "@hitachivantara/uikit-react-lab/dist/Table/hooks";

import useStyles from "./styles";

type TableCellProps = HvTableCellProps & {
  isDragOccurring: boolean;
  isDragging: boolean;
  cellId: string;
  children: React.ReactNode;
};

type TableCellSnapshot = {
  width: number;
  height: number;
};

type SnapshotMap = {
  [cellId: string]: TableCellSnapshot;
};

const snapshotMap: SnapshotMap = {};

class TableCell extends React.Component<TableCellProps> {
  ref?: HTMLElement;

  componentDidMount() {
    const { cellId } = this.props;
    if (!snapshotMap[cellId]) {
      return;
    }

    if (!this.props.isDragging) {
      // cleanup the map if it is not being used
      delete snapshotMap[cellId];
      return;
    }

    this.applySnapshot(snapshotMap[cellId]);
  }

  getSnapshotBeforeUpdate(prevProps: TableCellProps): TableCellSnapshot | null {
    // we will be locking the dimensions of the dragging item on mount
    if (this.props.isDragging) {
      return null;
    }

    const isDragStarting: boolean =
      this.props.isDragOccurring && !prevProps.isDragOccurring;

    if (!isDragStarting) {
      return null;
    }

    return this.getSnapshot();
  }

  componentDidUpdate(
    prevProps: TableCellProps,
    prevState: unknown,
    snapshot?: TableCellSnapshot
  ) {
    const { ref } = this;
    if (!ref) {
      return;
    }

    if (snapshot) {
      this.applySnapshot(snapshot);
      return;
    }

    if (this.props.isDragOccurring) {
      return;
    }

    // inline styles not applied
    if (ref.style.width == null) {
      return;
    }

    // no snapshot and drag is finished - clear the inline styles
    ref.style.removeProperty("height");
    ref.style.removeProperty("width");
    ref.style.removeProperty("max-width");
  }

  componentWillUnmount() {
    const snapshot = this.getSnapshot();
    if (!snapshot) {
      return;
    }
    snapshotMap[this.props.cellId] = snapshot;
  }

  getSnapshot = (): TableCellSnapshot | null => {
    if (!this.ref) {
      return null;
    }

    const { width, height } = this.ref.getBoundingClientRect();

    const snapshot: TableCellSnapshot = {
      width,
      height,
    };

    return snapshot;
  };

  applySnapshot = (snapshot: TableCellSnapshot) => {
    const { ref } = this;

    if (!ref) {
      return;
    }

    if (ref.style.width === `${snapshot.width}px`) {
      return;
    }

    ref.style.width = `${snapshot.width}px`;
    ref.style.maxWidth = `${snapshot.width}px`;
    ref.style.height = `${snapshot.height}px`;
  };

  setRef = (ref?: HTMLElement) => {
    this.ref = ref;
  };

  render() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { children, isDragOccurring, isDragging, cellId, ...others } =
      this.props;

    return (
      <HvTableCell {...others} ref={this.setRef}>
        {children}
      </HvTableCell>
    );
  }
}

// Using a table as the portal so that we do not get react
// warnings when mounting a tr element
const table: HTMLElement = document.createElement("table");
Object.assign(table.style, {
  margin: "0",
  padding: "0",
  border: "0",
  height: "0",
  width: "0",
  borderSpacing: "0",
});
const tbody: HTMLElement = document.createElement("tbody");
table.appendChild(tbody);
document.body.appendChild(table);

interface DraggableRowProps<T extends Record<string, unknown>> {
  row: HvRowInstance<T>;
  rowProps: HvTableRowProps;
  provided: DraggableProvided;
  snapshot: DraggableStateSnapshot;
  invalidityAccessor?: string;
}

const DraggableRow = <T extends Record<string, unknown>>({
  row,
  rowProps,
  provided,
  snapshot,
  invalidityAccessor,
}: DraggableRowProps<T>): JSX.Element => {
  const classes = useStyles();

  const invalid =
    invalidityAccessor != null && row.original[invalidityAccessor];

  const child = (
    <HvTableRow
      ref={provided.innerRef}
      className={clsx(classes.tableRow, {
        [classes.tableRowSelected]: row.isSelected,
        [classes.tableRowDragging]: snapshot.isDragging,
        [classes.tableRowInvalid]: invalid,
      })}
      {...rowProps}
      {...provided.draggableProps}
    >
      {row.cells.map((cell) => (
        <TableCell
          cellId={cell.column.id}
          {...cell.getCellProps()}
          className={clsx(classes.tableCell, {
            [classes.tableCellCheckbox]: cell.column.variant === "checkbox",
            [classes.tableCellAction]: cell.column.variant === "actions",
          })}
          isDragOccurring={snapshot.isDragging}
          isDragging={snapshot.isDragging}
        >
          {cell.render("Cell", {
            dragHandleProps: provided.dragHandleProps,
            isSomethingDragging: snapshot.draggingOver,
          })}
        </TableCell>
      ))}
    </HvTableRow>
  );

  if (!snapshot.isDragging) {
    return child;
  }

  return ReactDOM.createPortal(child, tbody);
};

interface DragDropListProps<T extends Record<string, unknown>>
  extends HvTableOptions<T> {
  columns?: Array<HvTableColumnConfig<T>>;
  invalidityAccessor?: string;
  actions: ActionGeneric[];
  actionsCallback: (selectedRowIds: Record<string, boolean>) => void;
  emptyState: JSX.Element;
  onDragStart?: (initial: DragStart, provided: ResponderProvided) => void;
  onDragEnd: (result: DropResult, provided: ResponderProvided) => void;
}

const DragDropList = <T extends Record<string, unknown>>({
  columns,
  invalidityAccessor,
  data,
  actions,
  actionsCallback,
  emptyState,
  onDragStart,
  onDragEnd,
}: DragDropListProps<T>): JSX.Element => {
  const { t } = useTranslation("common");
  const classes = useStyles();

  const rowId = "id";
  const getRowId = useCallback((v: T): string => `${v[rowId]}`, [rowId]);

  const {
    getTableProps,
    getTableBodyProps,
    prepareRow,
    headers,
    rows,
    state: { selectedRowIds },
    getHvBulkActionsProps,
  } = useHvTable<T>(
    {
      columns,
      data,
      getRowId,
    },
    useHvRowSelection,
    useHvBulkActions
  );

  const Separator = useCallback(
    () => <tr className={classes.separator} />,
    [classes.separator]
  );

  const EmptyRow = useCallback(
    () => (
      <HvTableRow>
        <HvTableCell
          className={clsx(classes.noData, classes.tableCell)}
          // colSpan is missing in the TS definition
          {...{ colSpan: headers.length }}
        >
          {emptyState}
        </HvTableCell>
      </HvTableRow>
    ),
    [classes, headers.length, emptyState]
  );

  return (
    <>
      <HvBulkActions
        selectAllLabel={t("buttons.selectAll")}
        {...getHvBulkActionsProps()}
        numTotal={data?.length}
        numSelected={Object.keys(selectedRowIds).length}
        actions={actions}
        actionsCallback={() => actionsCallback(selectedRowIds)}
        checkboxProps={{
          disabled: data?.length === 0,
        }}
      />

      <HvTableContainer
        className={classes.tableContainer}
        style={{ overflow: "visible" }}
      >
        <HvTable {...getTableProps()} className={classes.table}>
          <HvTableHead className={classes.tableHead}>
            <HvTableRow className={clsx(classes.tableRow)}>
              {headers.map((col) => (
                <HvTableHeader
                  {...col.getHeaderProps()}
                  className={clsx(classes.tableHeader, {
                    [classes.tableCellCheckbox]: col.variant === "checkbox",
                    [classes.tableCellAction]: col.variant === "actions",
                  })}
                >
                  {col.render("Header")}
                </HvTableHeader>
              ))}
            </HvTableRow>
          </HvTableHead>
          <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
            <Droppable droppableId="table-body" direction="vertical">
              {(droppableProvided) => (
                <HvTableBody
                  {...getTableBodyProps()}
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                  className={classes.tableBody}
                >
                  {rows.length > 0 ? (
                    rows.map((row) => {
                      prepareRow(row);

                      const { key, ...rowProps } = row.getRowProps();

                      return (
                        <Draggable
                          draggableId={row.original.id as string}
                          key={key}
                          index={row.index}
                        >
                          {(draggableProvided, draggableSnapshot) => {
                            return (
                              <>
                                {row.index > 0 && <Separator />}

                                <DraggableRow
                                  row={row}
                                  rowProps={rowProps}
                                  provided={draggableProvided}
                                  snapshot={draggableSnapshot}
                                  invalidityAccessor={invalidityAccessor}
                                />
                              </>
                            );
                          }}
                        </Draggable>
                      );
                    })
                  ) : (
                    <EmptyRow />
                  )}

                  {droppableProvided.placeholder}
                </HvTableBody>
              )}
            </Droppable>
          </DragDropContext>
        </HvTable>
      </HvTableContainer>
    </>
  );
};

export default DragDropList;
